// ============================================================
// data/searchData.js
// 필터 "정의"와 정적 참조표만 보관. (실데이터는 API 로 받음 → 목데이터 없음)
//
// [역할]
//  - FILTER_GROUPS : 좌측 필터 레일 구성 정의
//  - ORGAN_SUBTYPES / ORGAN_MUTATIONS : 장기 선택 시 동적 옵션 기본값
//      (백엔드 filterOptions 가 오면 그걸 우선 사용, 없으면 이 기본값)
//  - labelFor : 코드값 → 표시 라벨
//
// [데이터 모델]
//  - 한 행(group) = 오가노이드(GBCC) + 배지(NGS 10~11)가 동일한 NGS 묶음
//  - 그룹 안에서 datatype E=WES / R=RNA → 한 행에 WES/RNA 배지 공존
//  - 세부 NGS 코드(계대/소스 다른 것)는 상세 Drawer 드롭다운에서 선택
//  - CRO 가능 = NGS→GBCC 매핑 후 Organoid_Info.cro_status 에 배지명 포함
//                또는 MappingSummary.croAvailable=1 (강제) — 판정은 백엔드
// ============================================================

/* ---------- 장기 → 서브타입 (장기 선택 시 동적) ---------- */
export const ORGAN_SUBTYPES = {
    Lung: ["NSCLC, ADC", "NSCLC, SqCC", "SCLC", "LCC"],
    Colon: ["Adenocarcinoma", "Mucinous", "Signet ring"],
    Pancreas: ["PDAC", "Acinar", "Neuroendocrine"],
    Ovary: ["HGSOC", "LGSOC", "Clear cell", "Endometrioid"],
    Gastric: ["Intestinal", "Diffuse", "Mixed"],
    Breast: ["Luminal A", "Luminal B", "HER2+", "TNBC"],
};

/* ---------- 암종별 주요 mutation (미팅 공유 최신본) ----------
 * 유방 12종(돌연변이가 잘 안 나와 후보를 늘림 — 동시다발 가능성은 낮음). */
export const ORGAN_MUTATIONS = {
    Lung: ["EGFR", "ERBB2", "KRAS", "TP53", "BRAF", "PIK3CA"],
    Colon: ["TP53", "APC", "KRAS", "SMAD4", "EGFR", "BRAF", "NRAS"],
    Pancreas: ["KRAS", "TP53", "SMAD4", "CDKN2A"],
    Ovary: ["ARID1A", "PIK3CA", "TP53", "KRAS", "BRCA1", "BRCA2", "BRAF", "APC", "EGFR", "MET"],
    Gastric: ["TP53", "PIK3CA", "ARID1A", "KRAS", "CDH1"],
    Breast: ["TP53", "PIK3CA", "APC", "EGFR", "CDH1", "BRCA1", "BRCA2", "ESR1", "PTEN", "GATA3", "MAP3K1", "PIK3R1"],
};

/* 전체 mutation 후보 (장기 미선택 시 기본 옵션) */
export const ALL_MUTATIONS = Array.from(
    new Set(Object.values(ORGAN_MUTATIONS).flat())
).sort();

/* ---------- 약물 목록 (백엔드 filterOptions.drugs 로 대체 가능) ---------- */
/* 데모용 정적 약물 목록. 실제 옵션은 백엔드 filterOptions.drugs 를 사용한다.
 * 폐암 약 위주라 다른 장기에서 잘못 노출되므로 폴백으로 쓰지 않는다. */
export const DRUGS = ["Gefitinib", "Afatinib", "Osimertinib", "Poziotinib", "Crizotinib", "Erlotinib"];

/* ---------- Fusion 관심유전자 (백엔드 filterOptions.fusionGenes = TargetGeneList 로 대체) ----------
 * 현재 7개, 추후 4개로 축소 예정. 백엔드가 실제 목록을 내려줌. */
export const FUSION_GENES = ["ALK", "FGFR1", "FGFR3", "NTRK", "RET", "ROS1"];

/* ---------- CNV type ---------- */
export const CNV_TYPES = ["Amplification", "Deletion"];

/* ---------- 라벨 매핑 ---------- */
/* ---------- CNV 대표 유전자 (버튼으로 고정 노출. 나머지는 검색으로 추가) ---------- */
export const CNV_REP_GENES = [
    "MET", "EGFR", "ERBB2", "PIK3CA", "CCND1",
    "CCND2", "CCNE1", "MDM2", "CDK4", "CDK6", "CCND3",
];

/* mutation 대표 유전자 (장기 무관 공통 대표 — 버튼 고정 노출) */
export const MUT_REP_GENES = Array.from(
    new Set(Object.values(ORGAN_MUTATIONS).flat())
).sort();

/* 값 → 번역키.
 * 번역키는 이 저장소 관례대로 "한국어 원문"이다(locals/ko·en/translation.json).
 * 화면에서는 t(labelFor(v)) 로 쓴다. 유전자명·약물명·장기코드 같은 데이터 값은
 * 여기에 넣지 않는다(번역 대상 아님). */
const LABELS = {
    M: "남성", F: "여성",
    Primary: "원발", Metastatic: "전이",
    N: "정상", T: "종양",
    up: "증가", down: "감소",
    yes: "예", no: "아니오",
    amp: "증폭", del: "결손",
    WES: "WES", RNA: "RNA-Seq",
    HIGH: "높음", MODERATE: "중간",
    // 오가노이드 채취(수술) 시점 기준 치료 이력.
    //   채취 이후의 치료는 오가노이드에 영향을 줄 수 없어 naive 로 본다.
    pre:   "채취 전 치료",
    naive: "치료 이력 없음",
};
export function labelFor(v) {
    return LABELS[v] || v;
}

/* ---------- 필터 그룹 정의 ----------
 * kind: "chips"(다중) | "range"(듀얼슬라이더) | "tristate"(포함/제외/해제) | "genevariant"(유전자+hgvsp)
 * dynamic: "subtype" | "mutation" → useSearchData 가 동적 옵션 주입
 */
/* label 은 번역키(한국어 원문)다. 화면에서 t(label) 로 출력한다.
 * organLock: true 인 그룹은 장기를 고르기 전까지 잠긴다(장기 그룹 자신과 CRO 는 예외). */
export const FILTER_GROUPS = [
    {
        key: "sample",
        label: "장기 · 샘플",
        icon: "Layers",
        fields: [
            { key: "organ", label: "장기", kind: "chips", options: Object.keys(ORGAN_SUBTYPES) },
            // Normal / Tumor — NGS 코드 샘플타입 자리 기준. 백엔드 filters.sampleNT
            { key: "sampleNT", label: "정상 · 종양 (N/T)", kind: "chips", options: ["N", "T"], organLock: true },
            { key: "subtype", label: "Subtype", kind: "chips", options: [], dynamic: "subtype", dropdown: true, organLock: true },
            // 예전 이름은 'Disease type'. 판정 로직(원발/전이)은 그대로 두고 이름만 바꿨다.
            { key: "dxType", label: "Sample type", kind: "chips", options: ["Primary", "Metastatic"], organLock: true },
            // CRO 가능은 기본으로 켜 두는 필터라 장기 미선택 상태에서도 잠그지 않는다.
            { key: "cro", label: "CRO 가능 샘플", kind: "chips", options: ["yes"] },
        ],
    },
    {
        key: "molecular",
        label: "분자 정보",
        icon: "Dna",
        fields: [
            { key: "msi", label: "MSI-High", kind: "chips", options: ["yes"], organLock: true },
            // Mutation: 대표 유전자 버튼 고정 + 검색으로 전체 유전자 추가. 클릭 포함→제외→해제, hgvsp 2차 필터.
            { key: "mutation", label: "Mutation (대표 버튼 · 검색으로 추가)", kind: "genevariant", options: [], dynamic: "mutGene", repGenes: MUT_REP_GENES, organLock: true },
            // CNV: 대표 유전자 버튼 고정 + 검색으로 전체 유전자 추가. Amp/Del 지정.
            { key: "cnv", label: "CNV (대표 버튼 · 검색으로 추가)", kind: "cnvgene", options: [], dynamic: "cnvGene", repGenes: CNV_REP_GENES, organLock: true },
        ],
    },
    {
        key: "drug",
        label: "Drug response (IC50)",
        icon: "Pill",
        fields: [
            { key: "drug", label: "약물 (검색·선택)", kind: "chips", options: [], dynamic: "drug", organLock: true },
        ],
    },
    {
        key: "treatment",
        label: "환자 치료 이력",
        icon: "Pill",
        fields: [
            // 환자가 받은 치료 약물/레지멘 (백엔드 filterOptions.txDrugs 로 옵션 주입)
            { key: "txDrug", label: "치료 약물 · 레지멘 (검색·선택)", kind: "chips", options: [], dynamic: "txDrug", organLock: true },
            // 채취 시점 기준 치료 단계.
            //   pre   : 오가노이드 채취(수술)일 이전에 치료 이력 있음
            //   naive : 치료 이력 없음 + 채취 이후에만 치료한 경우
            //   (채취 이후 치료는 오가노이드에 영향을 줄 수 없어 naive 로 본다 — 2026-09-17)
            { key: "txStage", label: "치료 단계 (오가노이드 채취 시점 기준)", kind: "chips", options: ["pre", "naive"], organLock: true },
        ],
    },
    {
        key: "pairing",
        label: "페어 샘플",
        icon: "Layers",
        fields: [
            { key: "paired", label: "Paired Normal (NT)", kind: "chips", options: ["yes", "no"], organLock: true },
            { key: "immune", label: "Immune cell pair", kind: "chips", options: ["yes", "no"], organLock: true },
            { key: "caf", label: "CAF pair", kind: "chips", options: ["yes", "no"], organLock: true },
        ],
    },
    {
        key: "patient",
        label: "환자 정보",
        icon: "User",
        fields: [
            { key: "gender", label: "성별", kind: "chips", options: ["M", "F"], organLock: true },
        ],
    },
];

/* 활성 필터 → 사람이 읽는 줄 목록.
 * 화면 상단 문장과 엑셀 첫 시트 머리말이 같은 내용을 쓰도록 여기 한 곳에서 만든다. */
export function filterSummaryLines({ chips = {}, mutState = {}, cnv = [], selectedDrug = null }, t = (x) => x) {
    const fieldLabel = {};
    FILTER_GROUPS.forEach((g) => g.fields.forEach((f) => { fieldLabel[f.key] = f.label; }));
    const lines = [];
    Object.keys(chips).forEach((k) => {
        const vs = chips[k];
        if (!vs || vs.length === 0) return;
        lines.push(`${t(fieldLabel[k] || k)}: ${vs.map((v) => t(labelFor(v))).join(", ")}`);
    });
    const inc = Object.keys(mutState).filter((g) => mutState[g] === "include");
    const exc = Object.keys(mutState).filter((g) => mutState[g] === "exclude");
    if (inc.length) lines.push(`Mutation ${t('포함')}: ${inc.join(", ")}`);
    if (exc.length) lines.push(`Mutation ${t('제외')}: ${exc.join(", ")}`);
    if (cnv.length) lines.push(`CNV: ${cnv.map((c) => `${c.gene}(${c.type === "Amplification" ? "Amp" : "Del"})`).join(", ")}`);
    if (selectedDrug) lines.push(`IC50: ${selectedDrug}`);
    return lines;
}

/* 엑셀 첫 시트 = 화면 목록과 같은 컬럼. key 는 백엔드 export 응답의 필드명. */
export const EXPORT_LIST_COLS = [
    { key: "Organoid (GBCC)", label: "Organoid (GBCC)" },
    { key: "NGS", label: "NGS (배지그룹)" },
    { key: "Organ", label: "장기" },
    { key: "Subtype", label: "Subtype" },
    { key: "Mutation", label: "Mutation" },
    { key: "IC50", label: "IC50" },
    { key: "Fusion", label: "Fusion" },
];

/* ---------- API 엔드포인트 ---------- */
export const API = {
    list:    "/api/integratedSearch/list",
    allKeys: "/api/integratedSearch/allKeys",
    export:  "/api/integratedSearch/export",
    detail:  "/api/integratedSearch/detail",
};